import React, { useEffect, useState } from 'react';
import { MessageCard } from './MessageCard';
import { peopleProfiles, aiMessageTemplates } from '../utils/mockData';
import { MessageSquareTextIcon, RefreshCwIcon, PlusIcon, SendIcon, ListIcon, CheckIcon, UserIcon, ClipboardIcon } from 'lucide-react';
import { MessageTemplateSelector } from './MessageTemplateSelector';
import { EngagementContext } from './EngagementContext';
import { OutreachSequence } from './OutreachSequence';
interface MessagesProps {
  selectedPersonId: number | null;
  interestingPeople: number[];
  onShowToast: (message: string, type: 'success' | 'error') => void;
}
export const Messages: React.FC<MessagesProps> = ({
  selectedPersonId,
  interestingPeople,
  onShowToast
}) => {
  const [selectedPerson, setSelectedPerson] = useState<number | null>(selectedPersonId);
  const [generating, setGenerating] = useState(false);
  const [contextInput, setContextInput] = useState('');
  const [tone, setTone] = useState('professional');
  const [messageLength, setMessageLength] = useState('medium');
  const [callToAction, setCallToAction] = useState('meeting');
  const [savedMessages, setSavedMessages] = useState<any[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSequence, setShowSequence] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<number | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [messageStatus, setMessageStatus] = useState<'draft' | 'ready' | 'sent'>('draft');
  useEffect(() => {
    if (selectedPersonId) {
      setSelectedPerson(selectedPersonId);
    }
  }, [selectedPersonId]);
  const relevantPeople = peopleProfiles.filter(person => interestingPeople.includes(person.id));
  const getMessagesForPerson = (personId: number) => {
    return [...aiMessageTemplates.filter(message => message.personId === personId), ...savedMessages.filter(message => message.personId === personId)];
  };
  const handleGenerateMessage = (additionalContext?: string) => {
    if (!selectedPerson) {
      onShowToast('Please select a person first', 'error');
      return;
    }
    setGenerating(true);
    // Scroll to top of message area on mobile
    if (window.innerWidth < 768) {
      setTimeout(() => {
        const messageArea = document.getElementById('message-area');
        if (messageArea) {
          messageArea.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
    // Simulate message generation delay
    setTimeout(() => {
      const person = relevantPeople.find(p => p.id === selectedPerson);
      if (!person) {
        setGenerating(false);
        return;
      }
      const newMessage = {
        id: aiMessageTemplates.length + savedMessages.length + 1,
        personId: selectedPerson || 0,
        postId: 0,
        subject: `${tone.charAt(0).toUpperCase() + tone.slice(1)} outreach to ${person.name}`,
        message: generateMessageText(person, additionalContext)
      };
      setSavedMessages(prev => [...prev, newMessage]);
      setGenerating(false);
      setContextInput('');
      setSelectedMessage(newMessage.id);
      setMessageStatus('ready');
      onShowToast('Message generated successfully', 'success');
    }, 1500);
  };
  const generateMessageText = (person: any, additionalContext?: string) => {
    // This is a simplified template - in a real app, this would use AI to generate personalized content
    const firstName = person.name.split(' ')[0];
    const toneVariations = {
      professional: {
        greeting: `Dear ${firstName},`,
        intro: `I hope this message finds you well.`,
        outro: `I look forward to the possibility of connecting.`,
        signature: `Best regards,\n[Your Name]`
      },
      casual: {
        greeting: `Hi ${firstName},`,
        intro: `Hope you're having a great day!`,
        outro: `Let me know what you think.`,
        signature: `Cheers,\n[Your Name]`
      },
      formal: {
        greeting: `Dear Mr./Ms. ${person.name.split(' ')[1]},`,
        intro: `I am writing to you regarding a matter of mutual professional interest.`,
        outro: `I would appreciate the opportunity to discuss this further at your convenience.`,
        signature: `Sincerely,\n[Your Name]\n[Your Position]`
      },
      friendly: {
        greeting: `Hey ${firstName},`,
        intro: `I noticed your recent activity on LinkedIn and thought I'd reach out.`,
        outro: `Would love to chat more about this!`,
        signature: `Talk soon,\n[Your Name]`
      }
    };
    const ctaVariations = {
      meeting: `Would you be available for a 15-minute call next week to discuss how [Your Company] might help ${person.company} with [Value Proposition]?`,
      feedback: `I'd really value your thoughts on this approach. Could you share your perspective based on your experience at ${person.company}?`,
      resource: `I've attached a resource that I think might be valuable for your work at ${person.company}. I'd be happy to walk you through it if you're interested.`,
      connect: `I'd love to connect and learn more about your work at ${person.company}.`
    };
    const selectedTone = toneVariations[tone as keyof typeof toneVariations];
    const selectedCta = ctaVariations[callToAction as keyof typeof ctaVariations];
    // Build the message with the appropriate length
    let messageBody = '';
    if (messageLength === 'short') {
      messageBody = `${selectedTone.intro} ${additionalContext ? `\n\n${additionalContext}` : ''}\n\n${contextInput ? `${contextInput}\n\n` : ''}${selectedCta}`;
    } else if (messageLength === 'medium') {
      messageBody = `${selectedTone.intro} I came across your profile and noticed your role at ${person.company}${person.title ? ` as ${person.title}` : ''}. ${additionalContext ? `\n\n${additionalContext}` : ''}\n\n${contextInput ? `${contextInput}\n\n` : ''}At [Your Company], we specialize in [Your Value Proposition] and have helped companies like yours improve [Specific Metric] by [Percentage/Result].\n\n${selectedCta}`;
    } else {
      messageBody = `${selectedTone.intro} I came across your profile and noticed your role at ${person.company}${person.title ? ` as ${person.title}` : ''}.\n\n${additionalContext ? `${additionalContext}\n\n` : ''}${contextInput ? `${contextInput}\n\n` : ''}At [Your Company], we specialize in [Your Value Proposition] and have helped companies like yours improve [Specific Metric] by [Percentage/Result].\n\nFor example, we recently worked with a ${person.company.toLowerCase().includes('tech') ? 'technology' : person.company.toLowerCase().includes('health') ? 'healthcare' : 'similar'} company and helped them [Specific Achievement]. I believe we could achieve similar results for ${person.company}.\n\n${selectedCta}`;
    }
    return `${selectedTone.greeting}\n\n${messageBody}\n\n${selectedTone.outro}\n\n${selectedTone.signature}`;
  };
  const handleSelectTemplate = (templateText: string) => {
    if (!selectedPerson) {
      onShowToast('Please select a person first', 'error');
      return;
    }
    const person = relevantPeople.find(p => p.id === selectedPerson);
    if (!person) return;
    let personalizedTemplate = templateText.replace(/\[Name\]/g, person.name.split(' ')[0]).replace(/\[Their Company\]/g, person.company);
    const newMessage = {
      id: aiMessageTemplates.length + savedMessages.length + 1,
      personId: selectedPerson,
      postId: 0,
      subject: `Template message for ${person.name}`,
      message: personalizedTemplate
    };
    setSavedMessages(prev => [...prev, newMessage]);
    setShowTemplates(false);
    setSelectedMessage(newMessage.id);
    setMessageStatus('ready');
    onShowToast('Template applied successfully', 'success');
  };
  const handleSaveMessage = (messageId: number, updatedText: string) => {
    setSavedMessages(prev => prev.map(msg => msg.id === messageId ? {
      ...msg,
      message: updatedText
    } : msg));
    onShowToast('Message saved', 'success');
  };
  const handleSendMessage = () => {
    if (!selectedMessage) {
      onShowToast('Please select a message to send', 'error');
      return;
    }
    setMessageStatus('sent');
    onShowToast('Message sent successfully!', 'success');
  };
  const handleCreateSequence = () => {
    if (!selectedMessage) {
      onShowToast('Please select a message for your sequence', 'error');
      return;
    }
    setShowSequence(true);
  };
  const selectedMessageText = selectedMessage ? [...aiMessageTemplates, ...savedMessages].find(m => m.id === selectedMessage)?.message || '' : '';
  const selectedPersonName = relevantPeople.find(p => p.id === selectedPerson)?.name || '';
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-555">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 sm:gap-0" data-id="element-556">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-557">
          Outreach Messages
        </h1>
        {/* Action buttons */}
        <div className="flex space-x-2" data-id="element-558">
          <button onClick={() => setShowTemplates(!showTemplates)} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-559">
            <PlusIcon size={16} className="mr-2" data-id="element-560" />
            Templates
          </button>
          {selectedMessage && !showSequence && <button onClick={handleSendMessage} disabled={messageStatus === 'sent'} className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${messageStatus === 'sent' ? 'bg-green-600 text-white cursor-default' : 'bg-[#f9b30f] hover:bg-[#e0a40e] text-white'}`} data-id="element-561">
              {messageStatus === 'sent' ? <>
                  <CheckIcon size={16} className="mr-2" data-id="element-562" />
                  Sent
                </> : <>
                  <SendIcon size={16} className="mr-2" data-id="element-563" />
                  Send
                </>}
            </button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-564">
        <div className="lg:col-span-1" data-id="element-565">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-566">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-567">
              Select Person
            </h2>
            {relevantPeople.length > 0 ? <div className="space-y-2" data-id="element-568">
                <select value={selectedPerson || ''} onChange={e => {
              setSelectedPerson(Number(e.target.value) || null);
              setSelectedMessage(null);
              setMessageStatus('draft');
              setShowSequence(false);
            }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" data-id="element-569">
                  <option value="" data-id="element-570">Select a person</option>
                  {relevantPeople.map(person => <option key={person.id} value={person.id} data-id="element-571">
                      {person.name} - {person.title}
                    </option>)}
                </select>
              </div> : <div className="py-4 text-center" data-id="element-572">
                <div className="mb-3 flex justify-center" data-id="element-573">
                  <UserIcon size={24} className="text-gray-400" data-id="element-574" />
                </div>
                <p className="text-sm text-gray-500 mb-3" data-id="element-575">
                  No people marked as interesting yet
                </p>
                <p className="text-xs text-gray-400" data-id="element-576">
                  Mark people as "Interesting" in the People tab to create
                  messages for them
                </p>
              </div>}
            {selectedPerson && <div className="mt-4" data-id="element-577">
                <div className="flex justify-between items-center mb-2" data-id="element-578">
                  <h3 className="text-sm font-medium text-gray-700" data-id="element-579">
                    Message Context
                  </h3>
                  <button onClick={() => setShowSettings(!showSettings)} className="text-xs text-[#f9b30f] hover:text-[#e0a40e] font-medium" data-id="element-580">
                    {showSettings ? 'Hide Settings' : 'Show Settings'}
                  </button>
                </div>
                {showSettings && <div className="mb-4 space-y-3" data-id="element-581">
                    <div data-id="element-582">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-583">
                        Tone
                      </label>
                      <select className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" value={tone} onChange={e => setTone(e.target.value)} data-id="element-584">
                        <option value="professional" data-id="element-585">Professional</option>
                        <option value="casual" data-id="element-586">Casual</option>
                        <option value="formal" data-id="element-587">Formal</option>
                        <option value="friendly" data-id="element-588">Friendly</option>
                      </select>
                    </div>
                    <div data-id="element-589">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-590">
                        Length
                      </label>
                      <select className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" value={messageLength} onChange={e => setMessageLength(e.target.value)} data-id="element-591">
                        <option value="short" data-id="element-592">Short (50-100 words)</option>
                        <option value="medium" data-id="element-593">Medium (100-150 words)</option>
                        <option value="long" data-id="element-594">Long (150-200 words)</option>
                      </select>
                    </div>
                    <div data-id="element-595">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-596">
                        Call to Action
                      </label>
                      <select className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" value={callToAction} onChange={e => setCallToAction(e.target.value)} data-id="element-597">
                        <option value="meeting" data-id="element-598">Request Meeting</option>
                        <option value="feedback" data-id="element-599">Ask for Feedback</option>
                        <option value="resource" data-id="element-600">Share Resource</option>
                        <option value="connect" data-id="element-601">Connect Only</option>
                      </select>
                    </div>
                  </div>}
                <textarea value={contextInput} onChange={e => setContextInput(e.target.value)} placeholder="Add any specific information you'd like to include in the outreach message..." className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm" rows={4} data-id="element-602" />
                <button onClick={() => handleGenerateMessage()} disabled={generating} className="mt-3 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#f9b30f] hover:bg-[#e0a40e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f9b30f] disabled:opacity-50" data-id="element-603">
                  {generating ? <>
                      <RefreshCwIcon size={16} className="mr-2 animate-spin" data-id="element-604" />
                      Generating...
                    </> : 'Generate New Message'}
                </button>
              </div>}
          </div>
          {selectedPerson && <EngagementContext personId={selectedPerson} onGenerateMessage={handleGenerateMessage} data-id="element-605" />}
          {showTemplates && <div className="mt-6" data-id="element-606">
              <MessageTemplateSelector onSelectTemplate={handleSelectTemplate} data-id="element-607" />
            </div>}
        </div>
        <div id="message-area" className="lg:col-span-2" data-id="element-608">
          {selectedPerson ? showSequence ? <OutreachSequence personId={selectedPerson} personName={selectedPersonName} initialMessage={selectedMessageText} onShowToast={onShowToast} data-id="element-609" /> : <>
                <div className="mb-4 flex justify-between items-center" data-id="element-610">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-611">
                    Messages for {selectedPersonName}
                  </h2>
                  {selectedMessage && messageStatus !== 'sent' && <button onClick={handleCreateSequence} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-612">
                      <ListIcon size={14} className="mr-1" data-id="element-613" />
                      Create Sequence
                    </button>}
                </div>
                {messageStatus === 'sent' && <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-md flex items-center" data-id="element-614">
                    <CheckIcon className="text-green-500 mr-2" size={18} data-id="element-615" />
                    <div data-id="element-616">
                      <p className="text-sm text-green-700 font-medium" data-id="element-617">
                        Message sent successfully
                      </p>
                      <p className="text-xs text-green-600" data-id="element-618">
                        Your message has been sent to {selectedPersonName}
                      </p>
                    </div>
                  </div>}
                <div className="space-y-4" data-id="element-619">
                  {getMessagesForPerson(selectedPerson).length > 0 ? getMessagesForPerson(selectedPerson).map(message => <MessageCard key={message.id} message={message} onSave={handleSaveMessage} isSelected={selectedMessage === message.id} onSelect={() => {
              setSelectedMessage(message.id);
              if (messageStatus === 'sent') {
                setMessageStatus('ready');
              }
            }} data-id="element-620" />) : generating ? <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-621">
                      <RefreshCwIcon size={36} className="mx-auto text-[#f9b30f] mb-4 animate-spin" data-id="element-622" />
                      <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-623">
                        Generating your message...
                      </h3>
                      <p className="text-gray-500" data-id="element-624">
                        Our AI is crafting a personalized message based on your
                        settings.
                      </p>
                    </div> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-625">
                      <MessageSquareTextIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-626" />
                      <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-627">
                        No messages generated yet
                      </h3>
                      <p className="text-gray-500" data-id="element-628">
                        Add context and click "Generate New Message" to create
                        outreach templates.
                      </p>
                    </div>}
                </div>
                {selectedMessage && <div className="mt-6 flex justify-end" data-id="element-629">
                    <button onClick={() => {
              navigator.clipboard.writeText(selectedMessageText);
              onShowToast('Message copied to clipboard', 'success');
            }} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-630">
                      <ClipboardIcon size={14} className="mr-1" data-id="element-631" />
                      Copy to Clipboard
                    </button>
                  </div>}
              </> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-632">
              <MessageSquareTextIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-633" />
              <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-634">
                Select a person to start
              </h3>
              <p className="text-gray-500 mb-6" data-id="element-635">
                Choose a person from the dropdown to generate personalized
                outreach messages
              </p>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 max-w-md mx-auto text-left" data-id="element-636">
                <h4 className="font-medium text-blue-800 mb-2" data-id="element-637">
                  How to use the message generator:
                </h4>
                <ol className="list-decimal list-inside text-sm text-blue-700 space-y-2" data-id="element-638">
                  <li data-id="element-639">Select a person from the dropdown</li>
                  <li data-id="element-640">Add any specific context about your outreach</li>
                  <li data-id="element-641">Click "Generate New Message"</li>
                  <li data-id="element-642">Edit the message if needed</li>
                  <li data-id="element-643">Send the message or create a sequence</li>
                </ol>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};